import { ExternalLinkIcon } from '@heroicons/react/outline';
import { CategoryScale, Chart as ChartJS, Filler, Legend, LinearScale, LineElement, PointElement, Title, Tooltip } from 'chart.js';
import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Card } from '../Card';
import { KeyValue } from '../KeyValue';
import { Text, TextType } from '../Text';
import { appendClassProps } from '../util';
import { displayNumber } from './util';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Filler, Legend);
const options = {
    responsive: true,
    layout: {
        padding: 8
    },
    tension: 0.3,
    plugins: {
        legend: {
            display: false
        }
    },
    elements: {
        point: {
            radius: 0
        }
    },
    scales: {
        y: {
            display: false
        },
        x: {
            display: false
        }
    },
    maintainAspectRatio: false
};
/**
 - Use card as a generic UI wrapper for visual organization
 */
export const Metric = ({ windowMode = false, transparent = false, showPlaceholder = false, numberFormatOptions, fillType, fill, lineColor = '#ADD644', value, prefix, units, description, tooltip, chartData, externalLink, loading, className }) => {
    const handleClickLink = () => {
        window.open(externalLink, '_blank')?.focus();
    };
    const displayChartData = useMemo(() => chartData?.x !== undefined &&
        chartData?.y !== undefined &&
        chartData?.x?.length > 0 &&
        chartData?.y?.length > 0, [chartData]);
    lineColor = !displayChartData && showPlaceholder ? '#D1D5DB' : lineColor;
    const data = {
        labels: showPlaceholder && !displayChartData
            ? ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
            : chartData?.x,
        datasets: [
            {
                data: showPlaceholder && !displayChartData
                    ? [0, 1, 2, 2, 3, 2, 2, 2, 3, 3]
                    : chartData?.y,
                borderColor: lineColor,
                fill: fillType !== undefined && fillType !== 'none',
                backgroundColor: (dataset) => {
                    const ctx = dataset.chart.ctx;
                    let bg = '#ffffff';
                    if (fillType === 'gradient') {
                        bg = ctx.createLinearGradient(0, 0, 0, 110);
                        bg.addColorStop(0, typeof fill === 'object'
                            ? fill?.start
                            : 'rgba(173, 214, 68, 0.75)');
                        bg.addColorStop(1, typeof fill === 'object' ? fill?.stop : 'rgba(173, 214, 68, 0)');
                    }
                    else if (fillType === 'solid') {
                        bg = fill ?? 'rgba(173, 214, 68, 0.5)';
                    }
                    return bg;
                }
            }
        ]
    };
    let display = displayNumber({
        num: value,
        numberFormatOptions
    });
    if (display.value === 'NaN' || value === undefined)
        display.value = value?.toString() || '?';
    return (React.createElement(Card, { "data-testid": "metric", className: `gap-4${appendClassProps(className)}`, transparent: transparent, windowMode: windowMode },
        React.createElement("div", { className: "h-full flex flex-col gap-2 items-center" },
            externalLink && (React.createElement(ExternalLinkIcon, { className: `self-end -my-2 cursor-pointer h-6 w-6 text-blue-800`, onClick: handleClickLink })),
            React.createElement("div", { className: "flex flex-row gap-2 items-center" },
                prefix && (React.createElement(Text, { type: TextType.custom, overrideColor: true, className: `text-xl text-blue-800` }, loading ? React.createElement(Skeleton, { className: "w-4" }) : prefix)),
                React.createElement(Text, { type: TextType.custom, overrideColor: true, className: `text-3xl md:text-5xl font-bold text-blue-800` }, loading ? (React.createElement("div", { className: "w-24" },
                    React.createElement(Skeleton, null))) : (display.value)),
                React.createElement(Text, { type: TextType.custom, overrideColor: true, className: `text-xl text-blue-800` },
                    display.suffix,
                    units)),
            React.createElement(KeyValue, { label: description, tooltip: tooltip, windowMode: windowMode }),
            React.createElement("div", { className: "min-h-0 flex-1 relative w-full chart-canvas" },
                displayChartData &&
                    (loading ? (React.createElement(Skeleton, { className: "w-full h-full" })) : (React.createElement(Line, { data: data, options: options }))),
                !displayChartData &&
                    showPlaceholder &&
                    (loading ? (React.createElement(Skeleton, { className: "w-full h-full" })) : (React.createElement(Line, { data: data, options: options })))),
            showPlaceholder && !displayChartData && (React.createElement(Text, { className: `text-gray-500`, overrideColor: true }, "No Data")))));
};
export * from './index.types';
//# sourceMappingURL=index.js.map